import React, { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useTranslation } from 'react-i18next';
import { authFunnelQueryParameters } from './platform/funnel';
import { isOidcCallbackRequest } from './platform/runtime';
import './AuthGuard.css';

const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const hasRedirected = useRef(false);

  const renderState = (title: string, message?: string, tone: 'default' | 'error' = 'default') => (
    <div className={`auth-guard-loader auth-guard-loader--${tone}`}>
      <div className="auth-guard-loader__content">
        <div className="auth-guard-loader__spinner" />
        <div className="auth-guard-loader__text">
          <h2 className="auth-guard-loader__title">{title}</h2>
          {message ? <p className="auth-guard-loader__message">{message}</p> : null}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.error) {
      if (!auth.activeNavigator && !isOidcCallbackRequest() && !hasRedirected.current) {
        hasRedirected.current = true;
        // GL-010: the auth surface emits signup_start only when the registration
        // method is known. Carry the anonymous first-touch dimensions through the
        // OIDC request so that event remains comparable with the app-side steps.
        auth.signinRedirect({ extraQueryParams: authFunnelQueryParameters() });
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, auth.activeNavigator, auth]);

  if (auth.isLoading) {
    return renderState(t('authGuardLoading'));
  }

  if (auth.error) {
    return renderState(t('authGuardErrorTitle'), auth.error.message, 'error');
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  return renderState(t('authGuardRedirecting'));
};

export default AuthGuard;
